import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addMonths, eachDayOfInterval, endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { Check, ChevronLeft, ChevronRight, Info, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { clinicDateKey } from '../utils/clinicTime';

interface FreeRoom { roomId: number; roomName: string; hourlyRate: number; free: Array<{ start: string; end: string }> }
interface FreeDay { date: string; rooms: FreeRoom[] }
interface Quote {
  days: Array<{ date: string; hours: number; base: number; discountPercent: number; discount: number; total: number; existingHours: number }>;
  totals: { hours: number; base: number; discount: number; total: number };
  rate: number;
}
type Span = { start: number; end: number };

const money = (n: number) => `$${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(Math.round(n))}`;
const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const toStr = (n: number) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
const upper = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const shortDate = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

/** Time that is free in *every* list (the hours that work for all the chosen days). */
const intersect = (lists: Span[][]): Span[] => lists.reduce<Span[]>((acc, list, i) => {
  if (i === 0) return list;
  const out: Span[] = [];
  for (const a of acc) for (const b of list) {
    const s = Math.max(a.start, b.start), e = Math.min(a.end, b.end);
    if (e - s >= 30) out.push({ start: s, end: e });
  }
  return out;
}, []);

/**
 * Rent a consulting room: pick days, a room and hours, see exactly what it will cost (with the daily discount),
 * and confirm. Replaces the old single-day and "multiple days" screens.
 */
const RentRooms: React.FC = () => {
  const { professional } = useAuth();
  const [month, setMonth] = useState(new Date());
  const [free, setFree] = useState<Map<string, FreeDay>>(new Map());
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [dates, setDates] = useState<string[]>([]);
  const [roomId, setRoomId] = useState<number | null>(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const todayKey = clinicDateKey(new Date());

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await api.get('/postgres/professionals/rooms/free', {
        params: { from: format(startOfMonth(month), 'yyyy-MM-dd'), to: format(endOfMonth(month), 'yyyy-MM-dd') },
      });
      setFree(new Map((r.data.days as FreeDay[]).map(d => [d.date, d])));
      setState('ok');
    } catch {
      setState('error');
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  // rooms offered for the chosen days (free in all of them), with the hours that work everywhere
  const rooms = useMemo(() => {
    const names = new Map<number, { roomId: number; roomName: string; hourlyRate: number }>();
    free.forEach(d => d.rooms.forEach(r => names.set(r.roomId, { roomId: r.roomId, roomName: r.roomName, hourlyRate: r.hourlyRate })));
    return Array.from(names.values()).map(room => {
      const perDay = dates.map(d => (free.get(d)?.rooms.find(r => r.roomId === room.roomId)?.free || []).map(f => ({ start: toMin(f.start), end: toMin(f.end) })));
      const common = dates.length ? intersect(perDay) : [];
      const missing = dates.filter((_, i) => perDay[i].length === 0);
      return { ...room, common, missing };
    });
  }, [free, dates]);

  const room = rooms.find(r => r.roomId === roomId) || null;
  const startMin = start ? toMin(start) : null;
  const endMin = end ? toMin(end) : null;
  const block = room && startMin !== null ? room.common.find(b => startMin >= b.start && startMin < b.end) : undefined;
  const valid = !!(room && block && startMin !== null && endMin !== null && endMin > startMin && endMin <= block.end && dates.length > 0);

  // keep the chosen hours valid when the days/room change
  useEffect(() => {
    if (!room) { setStart(''); setEnd(''); return; }
    if (start && end && room.common.some(b => toMin(start) >= b.start && toMin(end) <= b.end)) return;
    setStart(''); setEnd('');
  }, [room, start, end]);

  // live price (debounced); the server is the single source of truth for the number
  useEffect(() => {
    if (!valid) { setQuote(null); return; }
    setQuoting(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.post('/postgres/professionals/bookings/quote', { dates, room: roomId, intervals: [{ start_time: start, end_time: end }] });
        setQuote(r.data);
      } catch { setQuote(null); } finally { setQuoting(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [valid, dates, roomId, start, end]);

  const toggleDate = (key: string) => { setProblem(''); setDates(d => d.includes(key) ? d.filter(x => x !== key) : [...d, key].sort()); };
  const preset = (s: number, e: number) => {
    if (!room) return;
    const b = room.common.find(c => c.start < e && c.end > s);
    if (!b) return;
    setStart(toStr(Math.max(s, b.start))); setEnd(toStr(Math.min(e, b.end)));
  };

  const confirm = async () => {
    if (!valid || !professional) return;
    setBusy(true); setProblem('');
    try {
      await api.post('/postgres/professionals/bookings', {
        professional_id: Number(professional.id), dates, rooms: [roomId], intervals: [{ start_time: start, end_time: end }],
      });
      toast.success(dates.length === 1 ? 'Consultorio alquilado' : `Alquilaste el consultorio ${dates.length} días`);
      setDates([]); setRoomId(null); setStart(''); setEnd(''); setQuote(null);
      await load();
    } catch (e: any) {
      setProblem(e.response?.data?.error || 'No pudimos completar el alquiler. Probá de nuevo.');
      await load();
    } finally { setBusy(false); }
  };

  const days = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) });
  const lead = (startOfMonth(month).getDay() + 6) % 7;
  // "every Tuesday of this month": same weekday as the last picked day, only days that still have room
  const lastPicked = dates.length ? dates[dates.length - 1] : null;
  const sameWeekday = lastPicked ? days.filter(d => d.getDay() === new Date(`${lastPicked}T12:00:00`).getDay() && free.has(format(d, 'yyyy-MM-dd'))).map(d => format(d, 'yyyy-MM-dd')) : [];
  const weekdayName = lastPicked ? format(new Date(`${lastPicked}T12:00:00`), 'EEEE', { locale: es }) : '';
  const addAllSameWeekday = () => { setProblem(''); setDates(d => Array.from(new Set([...d, ...sameWeekday])).sort()); };
  const blocks = room?.common || [];
  const hoursOptions = (from: number, to: number) => { const o: string[] = []; for (let t = from; t <= to; t += 30) o.push(toStr(t)); return o; };

  return (
    <div className="space-y-6 pb-28">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Alquilar consultorio</h2>
        <p className="mt-1 text-sm text-gray-500">Elegí los días, el consultorio y el horario. Ves el precio antes de confirmar.</p>
      </div>

      {/* 1. days */}
      <section className="card p-5" aria-labelledby="step-days">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="step-days" className="text-lg font-semibold text-gray-900"><span className="mr-2 text-accent-orange">1</span>¿Qué días?</h3>
          <div className="flex items-center gap-2">
            <button className="btn btn-outline btn-md" aria-label="Mes anterior" onClick={() => setMonth(subMonths(month, 1))}><ChevronLeft className="h-4 w-4" /></button>
            <span className="min-w-[8.5rem] text-center font-semibold">{upper(format(month, 'MMMM yyyy', { locale: es }))}</span>
            <button className="btn btn-outline btn-md" aria-label="Mes siguiente" onClick={() => setMonth(addMonths(month, 1))}><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-500">Tocá uno o varios días con lugar libre.</p>

        {state === 'loading' && <div className="mt-4 h-56 animate-pulse rounded-xl bg-gray-100" />}
        {state === 'error' && <p className="mt-4 text-center text-gray-500">No pudimos cargar los horarios. <button className="font-semibold underline" onClick={load}>Reintentar</button></p>}
        {state === 'ok' && (<>
          <div className="mt-4 grid grid-cols-7 text-center text-xs font-bold uppercase tracking-wide text-gray-400">
            {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {Array.from({ length: lead }).map((_, i) => <div key={`b${i}`} />)}
            {days.map(d => {
              const key = format(d, 'yyyy-MM-dd');
              const info = free.get(key);
              const sel = dates.includes(key);
              return (
                <button
                  key={key} type="button" disabled={!info} onClick={() => toggleDate(key)} aria-pressed={sel}
                  aria-label={info ? `${format(d, "d 'de' MMMM", { locale: es })}: ${info.rooms.length} consultorio(s) con lugar` : `${format(d, "d 'de' MMMM", { locale: es })}: sin lugar`}
                  className={`flex aspect-square flex-col items-center justify-center rounded-xl border text-sm transition-colors
                    ${sel ? 'border-primary-600 bg-primary-600 text-white' : info ? 'border-gray-200 bg-white hover:border-primary-300' : 'border-transparent text-gray-300'}
                    ${key === todayKey && !sel ? 'ring-2 ring-accent-gold' : ''}`}
                >
                  <span className="font-semibold">{format(d, 'd')}</span>
                  {info && <span className={`text-[10px] leading-none ${sel ? 'text-white/90' : 'text-green-700'}`}>{sel ? <Check className="inline h-3 w-3" aria-hidden="true" /> : `${info.rooms.length} lib.`}</span>}
                </button>
              );
            })}
          </div>
          {dates.length > 0 && (
            <p className="mt-3 text-sm text-gray-600">
              <b>{dates.length}</b> día{dates.length !== 1 ? 's' : ''}: {dates.map(shortDate).join(', ')}{' '}
              <button className="font-semibold text-primary-700 underline" onClick={() => setDates([])}>Borrar selección</button>
              {sameWeekday.some(k => !dates.includes(k)) && (
                <>{' · '}<button className="font-semibold text-primary-700 underline" onClick={addAllSameWeekday}>Sumar todos los {weekdayName} de este mes</button></>
              )}
            </p>
          )}
        </>)}
      </section>

      {/* 2. room + hours */}
      {dates.length > 0 && (
        <section className="card p-5" aria-labelledby="step-room">
          <h3 id="step-room" className="text-lg font-semibold text-gray-900"><span className="mr-2 text-accent-orange">2</span>¿Qué consultorio y horario?</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Consultorio">
            {rooms.map(r => {
              const usable = r.common.length > 0;
              return (
                <button
                  key={r.roomId} type="button" role="radio" aria-checked={roomId === r.roomId} disabled={!usable}
                  onClick={() => { setRoomId(r.roomId); setProblem(''); }}
                  className={`rounded-xl border p-4 text-left transition-colors ${roomId === r.roomId ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-600/20' : usable ? 'border-gray-200 bg-white hover:border-primary-300' : 'border-gray-100 bg-gray-50 opacity-60'}`}
                >
                  <div className="font-semibold text-gray-900">{r.roomName}</div>
                  <div className="text-sm text-gray-600">{money(r.hourlyRate)} por hora</div>
                  <div className={`mt-1 text-xs ${usable ? 'text-green-700' : 'text-amber-800'}`}>
                    {usable ? `Libre ${r.common.map(c => `${toStr(c.start)}–${toStr(c.end)}`).join(' y ')}`
                      : r.missing.length ? `Ocupado el ${r.missing.map(shortDate).join(', ')}` : 'Sin horarios en común'}
                  </div>
                </button>
              );
            })}
          </div>

          {room && blocks.length > 0 && (
            <div className="mt-5">
              <p className="text-sm font-semibold text-gray-700">Horario (el mismo todos los días elegidos)</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {blocks.map(b => (
                  <button key={`all-${b.start}`} type="button" className="btn btn-sm btn-outline" onClick={() => preset(b.start, b.end)}>Todo {toStr(b.start)}–{toStr(b.end)}</button>
                ))}
                {blocks.some(b => b.start < 13 * 60) && <button type="button" className="btn btn-sm btn-outline" onClick={() => preset(0, 13 * 60)}>Mañana (hasta 13:00)</button>}
                {blocks.some(b => b.end > 14 * 60) && <button type="button" className="btn btn-sm btn-outline" onClick={() => preset(14 * 60, 24 * 60)}>Tarde (desde 14:00)</button>}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <label className="flex items-center gap-2">Desde
                  <select className="input h-10 w-auto" value={start} onChange={e => { setStart(e.target.value); if (end && toMin(end) <= toMin(e.target.value)) setEnd(''); }}>
                    <option value="">--:--</option>
                    {blocks.flatMap(b => hoursOptions(b.start, b.end - 30)).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-2">Hasta
                  <select className="input h-10 w-auto" value={end} onChange={e => setEnd(e.target.value)} disabled={!block}>
                    <option value="">--:--</option>
                    {block && hoursOptions(block.start, block.end).filter(t => toMin(t) > (startMin ?? 0)).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>
            </div>
          )}
        </section>
      )}

      {/* 3. price + confirm (sticky on phones) */}
      {valid && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur lg:static lg:rounded-2xl lg:border lg:px-5 lg:shadow-sm" role="region" aria-label="Resumen del alquiler">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <p className="font-semibold text-gray-900">
                {room!.roomName} · {start}–{end} · {dates.length} día{dates.length !== 1 ? 's' : ''} · {quote ? `${quote.totals.hours} h` : '…'}
              </p>
              {quote && quote.totals.discount > 0 && (
                <p className="text-green-700">Incluye un descuento de {money(quote.totals.discount)} ({quote.days.find(d => d.discountPercent)?.discountPercent}% por día)</p>
              )}
              {quote && quote.totals.discount === 0 && quote.days.some(d => d.hours + d.existingHours >= 1) && (
                <p className="flex items-center gap-1 text-gray-500"><Info className="h-3.5 w-3.5" aria-hidden="true" />Sin descuento: se aplica al llegar a las horas mínimas por día en un mismo consultorio.</p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs text-gray-500">Total de este alquiler</div>
                <div className="text-2xl font-bold text-primary-700">{quoting || !quote ? '…' : money(quote.totals.total)}</div>
              </div>
              <button type="button" className="btn btn-cta btn-lg" disabled={busy || !quote} onClick={confirm}>{busy ? 'Alquilando…' : 'Confirmar alquiler'}</button>
            </div>
          </div>
        </div>
      )}

      {problem && (
        <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /><span>{problem}</span>
        </div>
      )}

      <p className="rounded-xl bg-primary-50 px-4 py-3 text-sm text-primary-900">
        ¿Necesitás un horario que ya está alquilado? Mirá quién lo tiene en el <Link className="font-semibold underline" to="../rooms">Calendario de consultorios</Link> y escribile para coordinar.
      </p>

      <p className="text-sm text-gray-500">
        Lo que alquilás se paga: no se puede liberar ni cancelar desde acá. Si necesitás un cambio, hablalo con administración.
        Se suma a tu total del mes en <Link className="font-semibold text-primary-700 underline" to="../monthly-summary">Mis cobros</Link>.
      </p>
    </div>
  );
};

export default RentRooms;
